// src/views/NewGangRunView.js
// Crear un Gang Run / Combo (4ª opción de New Job) — PLAN-GANG-RUN Fase A (PTO).
// La unidad es Cliente + SKU del catálogo (trae el 1UP/dieline) + cantidad + ups.
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import GangLinesEditor from "../components/GangRun/GangLinesEditor";
import { createGangRun } from "../lib/gangrun";
import { resolveCategories } from "../lib/productCategories";

const NewGangRunView = ({ db, user, storage }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({ jobName: "", materialId: "", width: "", height: "", unit: "in" });
  const [lines, setLines] = useState([]);

  const load = useCallback(async () => {
    const [cSnap, pSnap, mSnap, cfg] = await Promise.all([
      getDocs(collection(db, "clients")),
      getDocs(collection(db, "products")),
      getDocs(collection(db, "materials")),
      getDoc(doc(db, "project_settings", "productCategories")),
    ]);
    setClients(cSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    setProducts(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setMaterials(mSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    setCategories(resolveCategories(cfg.exists() ? cfg.data().categories : null));
  }, [db]);

  useEffect(() => { load().catch(() => setError("No se pudo cargar.")).finally(() => setLoading(false)); }, [load]);

  const sheet = { width: Number(form.width) || null, height: Number(form.height) || null, unit: form.unit };

  const handleCreate = async () => {
    setError("");
    if (lines.length === 0) return setError("Agrega al menos una línea (cliente + SKU).");
    setSaving(true);
    try {
      const mat = materials.find((m) => m.id === form.materialId);
      const jobId = await createGangRun(db, {
        jobName: form.jobName.trim(), materialId: form.materialId || null, materialName: mat?.name || "", sheet, lines,
      }, user);
      navigate(`/jobs/${jobId}`);
    } catch (err) { setError(err.message || "No se pudo crear el gang run."); setSaving(false); }
  };

  const inputCls = "w-full p-3 text-base border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

  return (
    <div className="max-w-5xl mx-auto p-2">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Nuevo Trabajo · Gang Run / Combo</h1>
        <p className="text-sm text-gray-500">Una hoja con piezas (SKU) de varios clientes que comparten material. Producir-a-orden (PTO).</p>
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className={labelCls}>Nombre (opcional)</label>
            <input className={inputCls} value={form.jobName} onChange={(e) => setForm({ ...form, jobName: e.target.value })} placeholder="Ej. Gang B-Flute semana 24" />
          </div>
          <div>
            <label className={labelCls}>Material</label>
            <select className={inputCls} value={form.materialId} onChange={(e) => setForm({ ...form, materialId: e.target.value })} disabled={loading}>
              <option value="">— Material —</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Hoja (An×Al)</label>
            <div className="flex gap-1">
              <input type="number" className={inputCls} value={form.width} onChange={(e) => setForm({ ...form, width: e.target.value })} placeholder="An" />
              <input type="number" className={inputCls} value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} placeholder="Al" />
              <select className="p-3 border border-gray-300 rounded-lg" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                <option value="in">in</option><option value="mm">mm</option><option value="ft">ft</option>
              </select>
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="font-semibold text-gray-700 mb-2">Piezas en la hoja (cliente + SKU)</h3>
          <GangLinesEditor db={db} user={user} storage={storage} gangMaterialId={form.materialId} clients={clients} products={products} categories={categories} materials={materials} sheet={sheet} lines={lines} onChange={setLines} reload={load} />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <button type="button" onClick={() => navigate("/jobs/new")} className="px-5 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold">Cancelar</button>
          <button type="button" onClick={handleCreate} disabled={saving || lines.length === 0} className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold disabled:opacity-50">
            {saving ? "Creando…" : "Crear Gang Run"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewGangRunView;
